<template>
  <div class="w-full" style="min-height: 600px">
    
    <template v-if="isSpecialPage">
        <inancComponent></inancComponent>
    </template>
    <template v-else-if="isGuideLanding">
      <div class="guide-landing bg-[#f5f6f2] pb-16">
        <section class="relative isolate min-h-[520px] overflow-hidden bg-[#072f33] text-white md:min-h-[600px]">
          <img
            src="/assets/img/guides/umre-hac-rehberi-hero.webp"
            alt="Kâbe ve Mescid-i Haram"
            class="absolute inset-0 h-full w-full object-cover object-[62%_center]"
            fetchpriority="high"
          />
          <div class="absolute inset-0 bg-gradient-to-r from-[#052b2f] via-[#052b2f]/90 to-[#052b2f]/10"></div>
          <div class="absolute inset-0 guide-grid opacity-20"></div>
          <div class="relative mx-auto flex min-h-[520px] max-w-screen-xl flex-col justify-between px-4 pb-10 pt-7 sm:px-6 md:min-h-[600px] md:pb-14 lg:px-10">
            <div class="flex items-center justify-between gap-4">
              <JourneyPageLinks current="spiritual" variant="breadcrumb" dark />
              <GlobalShare :title="`${$t('Seninle bunu paylaşıyorum')} ${pageData?.SA_Baslik} ${url}`" :url="url"></GlobalShare>
            </div>

            <div class="max-w-2xl py-12 md:py-16">
              <div class="mb-5 inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[.18em] text-amber-200 backdrop-blur">
                <font-awesome-icon icon="fa-solid fa-kaaba" class="h-3 w-3" />
                Adım adım manevi hazırlık
              </div>
              <h1 class="text-4xl font-black leading-[1.07] tracking-tight text-white md:text-6xl" v-html="pageData?.SA_Baslik || $t('Umre Manevi Rehberi')"></h1>
              <p class="mt-5 max-w-xl text-sm leading-7 text-white/75 md:text-lg md:leading-8">Niyetten ibadete, hazırlıktan kutsal topraklardaki uygulamalara kadar ihtiyaç duyacağınız güvenilir bilgiler tek bir rehberde.</p>
              <div class="mt-8 flex flex-wrap gap-3">
                <a href="#guide-topics" class="inline-flex items-center gap-2 rounded-xl bg-amber-400 px-5 py-3 text-sm font-extrabold text-[#123d3c] shadow-lg shadow-black/10 transition hover:-translate-y-0.5 hover:bg-amber-300">Rehberi keşfet <font-awesome-icon icon="fa-solid fa-arrow-down" class="h-3 w-3" /></a>
                <a :href="getPath('/tour/umre-turlari')" class="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur transition hover:bg-white/20" @click.prevent="goPath('/tour/umre-turlari')">Umre programları</a>
              </div>
            </div>

            <div class="grid max-w-2xl grid-cols-3 gap-2 md:gap-3">
              <div v-for="stat in guideStats" :key="stat.label" class="rounded-xl border border-white/15 bg-white/10 px-3 py-3 backdrop-blur-md md:rounded-2xl md:p-4">
                <div class="text-lg font-black text-amber-300 md:text-2xl">{{ stat.value }}</div>
                <div class="mt-0.5 text-[9px] leading-tight text-white/65 md:text-xs">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </section>

        <section id="guide-topics" class="px-4 py-6 sm:px-6 lg:px-10 lg:py-6">
          <div class="mx-auto max-w-screen-xl ">
          <div class="grid gap-10 lg:grid-cols-[1fr_320px]">
            <div>
              <div class="mb-8 max-w-2xl">
                <span class="text-xs font-extrabold uppercase tracking-[.2em] text-teal-700">Bilgiyle hazırlanın, huzurla yola çıkın</span>
                <h2 class="mt-2 text-3xl font-black tracking-tight text-slate-900 md:text-4xl">Rehber başlıkları</h2>
                <p class="mt-3 text-sm leading-7 text-slate-600">Her aşamada aradığınız bilgiye kolayca ulaşın. Başlıklar sade, anlaşılır ve yolculuk sırasına uygun biçimde düzenlendi.</p>
              </div>

              <div v-if="parentPages.parent?.length" class="grid gap-5 sm:grid-cols-2">
                <a
                  v-for="(page, index) in parentPages.parent"
                  :key="page.pdb_Id"
                  :href="getPath('/page/' + page.SA_Link)"
                  class="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_50px_-38px_rgba(15,23,42,.55)] transition duration-300 hover:-translate-y-1 hover:border-teal-300 hover:shadow-xl"
                  @click.prevent="goPath('/page/' + page.SA_Link)"
                >
                  <div class="relative h-44 overflow-hidden bg-gradient-to-br from-[#0b5557] to-[#062f32]">
                    <img v-if="page.SA_Fotograf" :src="showImage(page.SA_Fotograf, 720, 420, true, 88)" :alt="page.SA_Baslik" class="h-full w-full object-cover transition duration-700 group-hover:scale-105" loading="lazy" />
                    <img v-else src="/assets/img/guides/umre-hac-rehberi-hero.webp" :alt="page.SA_Baslik" class="h-full w-full object-cover opacity-55 transition duration-700 group-hover:scale-105" loading="lazy" />
                    <div class="absolute inset-0 bg-gradient-to-t from-[#052f32]/95 via-[#052f32]/20 to-transparent"></div>
                    <span class="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-xs font-black text-amber-200 backdrop-blur">{{ String(index + 1).padStart(2, '0') }}</span>
                    <h3 class="absolute bottom-4 left-4 right-4 text-xl font-black leading-tight text-white" v-html="page.SA_Baslik"></h3>
                  </div>
                  <div class="flex min-h-[110px] items-center justify-between gap-4 p-5">
                    <p class="mb-0 line-clamp-3 text-sm leading-6 text-slate-600" v-html="page.SA_Subtitle || 'Bu bölümdeki rehber yazılarını ve pratik bilgileri inceleyin.'"></p>
                    <span class="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-teal-50 text-teal-700 transition group-hover:bg-teal-700 group-hover:text-white"><font-awesome-icon icon="fa-solid fa-arrow-right" class="h-3 w-3" /></span>
                  </div>
                </a>
              </div>

              <div v-if="pageData?.SA_Yazilar" class="guide-prose article-prose prose mt-10 max-w-none rounded-3xl border border-slate-200 bg-white p-6 text-slate-700 shadow-sm md:p-8" v-html="YaziRevised"></div>
            </div>

            <aside class="lg:pt-3">
              <div class="sticky top-24 space-y-5">
                <div class="overflow-hidden rounded-3xl bg-[#073c3f] p-6 text-white shadow-xl">
                  <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-300 text-[#073c3f]"><font-awesome-icon icon="fa-solid fa-compass" /></div>
                  <h3 class="mt-5 text-xl font-black text-white">Yolculuğun üç adımı</h3>
                  <ol class="mt-5 space-y-5">
                    <li v-for="(step, index) in guideSteps" :key="step.title" class="flex gap-3">
                      <span class="flex h-7 w-7 flex-none items-center justify-center rounded-full border border-amber-300/40 bg-amber-300/10 text-[11px] font-black text-amber-200">{{ index + 1 }}</span>
                      <div><div class="text-sm font-extrabold">{{ step.title }}</div><p class="mb-0 mt-1 text-xs leading-5 text-white/60">{{ step.text }}</p></div>
                    </li>
                  </ol>
                </div>
                <div class="rounded-3xl border border-amber-200 bg-amber-50 p-5">
                  <div class="text-xs font-extrabold uppercase tracking-wider text-amber-700">Aklınıza takılan bir konu mu var?</div>
                  <p class="mb-4 mt-2 text-sm leading-6 text-slate-600">Deneyimli tur danışmanlarımız yolculuk öncesi sorularınızı yanıtlasın.</p>
                  <button type="button" class="w-full rounded-xl bg-amber-400 px-4 py-3 text-sm font-extrabold text-[#173f3d] hover:bg-amber-300" @click="contactForm.show = true">Bize danışın</button>
                </div>
              </div>
            </aside>
          </div>
          </div>
        </section>
      </div>
    </template>
    <template v-else-if="isUmrahGuideLanding">
      <div class="guide-landing umrah-guide-landing bg-[#f7f7f3] pb-14">
        <section class="relative isolate min-h-[500px] overflow-hidden bg-[#173f3d] text-white md:min-h-[570px]">
          <img src="/assets/img/guides/umre-rehberi-hero.webp" alt="Umre rehberi - Kâbe" class="absolute inset-0 h-full w-full object-cover object-[42%_center]" fetchpriority="high" />
          <div class="absolute inset-0 bg-gradient-to-l from-[#063b3d]/95 via-[#063b3d]/70 to-[#063b3d]/10"></div>
          <div class="absolute inset-0 guide-grid-right opacity-20"></div>
          <div class="relative mx-auto flex min-h-[500px] max-w-screen-xl flex-col px-4 pb-10 pt-7 sm:px-6 md:min-h-[570px] lg:px-10">
            <div class="flex items-center justify-between gap-4">
              <JourneyPageLinks current="practical" variant="breadcrumb" dark />
              <GlobalShare :title="`${$t('Seninle bunu paylaşıyorum')} ${pageData?.SA_Baslik} ${url}`" :url="url"></GlobalShare>
            </div>
            <div class="my-auto max-w-xl self-end py-10 md:pr-5">
              <div class="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[.17em] text-amber-200 backdrop-blur"><font-awesome-icon icon="fa-solid fa-book-open" class="h-3 w-3" /> Kapsamlı ve anlaşılır rehber</div>
              <h1 class="font-black tracking-tight text-white" v-html="pageData?.SA_Baslik || $t('Umre Rehberi')"></h1>
              <p class="mt-4 text-sm leading-7 text-white/80 md:text-base">Umreye nasıl hazırlanılır, ibadetler nasıl yapılır, yanınıza ne almalısınız? Yolculuk öncesi ve sırasında ihtiyaç duyacağınız pratik cevaplar burada.</p>
              <div class="mt-7 flex flex-wrap gap-3"><a href="#umrah-guide-topics" class="inline-flex items-center gap-2 rounded-xl bg-amber-400 px-5 py-3 text-sm font-extrabold text-[#173f3d] hover:bg-amber-300">Başlıkları incele <font-awesome-icon icon="fa-solid fa-arrow-down" class="h-3 w-3" /></a><button type="button" class="rounded-xl border border-white/20 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur hover:bg-white/20" @click="contactForm.show = true">Bir uzmana danış</button></div>
            </div>
          </div>
        </section>
        <section class="w-full  mx-auto py-0">
        <div class="umrah-guide-shortcuts relative z-10 mx-auto -mt-7 max-w-screen-xl px-4 sm:px-6 lg:px-10">
          <div class="grid overflow-hidden rounded-3xl border border-white bg-white shadow-[0_20px_60px_-38px_rgba(6,59,61,.5)] sm:grid-cols-2 lg:grid-cols-4">
            <div v-for="item in umrahGuideShortcuts" :key="item.title" class="group flex gap-3 border-b border-slate-100 p-4 last:border-0 sm:p-5 lg:border-b-0 lg:border-r lg:last:border-r-0">
              <span class="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-teal-50 text-teal-700 transition group-hover:bg-teal-700 group-hover:text-white"><font-awesome-icon :icon="item.icon" /></span>
              <div><h3 class="text-sm font-extrabold text-slate-800">{{ item.title }}</h3><p class="mt-1 text-xs leading-5 text-slate-500">{{ item.text }}</p></div>
            </div>
          </div>
        </div>

        <div id="umrah-guide-topics" class="umrah-guide-content mx-auto max-w-screen-xl scroll-mt-24 px-4 sm:px-6 lg:px-10">
          <div class="mb-8 flex flex-wrap items-end justify-between gap-5">
            <div class="max-w-2xl"><span class="text-xs font-extrabold uppercase tracking-[.2em] text-teal-700">Aradığınız bilgiye kolayca ulaşın</span><h2 class="mt-2 font-black tracking-tight text-slate-900">Umre rehberi başlıkları</h2><p class="mt-3 text-sm leading-7 text-slate-600">Hazırlıktan ibadetlere, Mekke ve Medine ziyaretlerinden sık sorulan sorulara kadar tüm içerikler düzenli bir yol haritasında.</p></div>
            <div class="rounded-full border border-teal-100 bg-teal-50 px-4 py-2 text-xs font-bold text-teal-800">{{ umrahGuidePages.length }} rehber bölümü</div>
          </div>

          <div v-if="umrahGuidePages.length" class="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            <a v-for="(page, index) in umrahGuidePages" :key="page.pdb_Id || page.SA_Link" :href="getPath('/page/' + page.SA_Link)" class="group relative flex min-h-[300px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_16px_45px_-35px_rgba(15,23,42,.55)] transition duration-300 hover:-translate-y-1 hover:border-amber-300 hover:shadow-xl" @click.prevent="goPath('/page/' + page.SA_Link)">
              <div class="relative h-44 overflow-hidden bg-[#0b5557]">
                <img v-if="page.SA_CardImage" :src="page.SA_CardImage" :alt="page.SA_Baslik" class="h-full w-full object-cover transition duration-700 group-hover:scale-105" loading="lazy" />
                <img v-else-if="page.SA_Fotograf" :src="showImage(page.SA_Fotograf, 720, 420, true, 88)" :alt="page.SA_Baslik" class="h-full w-full object-cover transition duration-700 group-hover:scale-105" loading="lazy" />
                <img v-else src="/assets/img/guides/umre-rehberi-hero.webp" :alt="page.SA_Baslik" class="h-full w-full object-cover opacity-65 transition duration-700 group-hover:scale-105" loading="lazy" />
                <div class="absolute inset-0 bg-gradient-to-t from-slate-950/65 via-transparent"></div><span class="absolute left-4 top-4 rounded-lg border border-white/20 bg-slate-950/25 px-2.5 py-1 text-[10px] font-black text-white backdrop-blur">{{ page.isFeaturedGuide ? 'ÖNE ÇIKAN' : `REHBER ${String(index + 1).padStart(2, '0')}` }}</span>
              </div>
              <div class="flex flex-1 flex-col p-5"><h3 class="font-black text-slate-900 transition group-hover:text-teal-700" v-html="page.SA_Baslik"></h3><p class="mt-2 line-clamp-3 text-sm leading-6 text-slate-600" v-html="page.SA_Subtitle || 'Bu bölümdeki pratik bilgileri ve rehber yazılarını inceleyin.'"></p><div class="mt-auto flex items-center justify-between border-t border-slate-100 pt-4 text-xs font-extrabold text-teal-700"><span>İçeriği aç</span><span class="flex h-8 w-8 items-center justify-center rounded-full bg-teal-50 transition group-hover:bg-teal-700 group-hover:text-white"><font-awesome-icon icon="fa-solid fa-arrow-right" class="h-3 w-3" /></span></div></div>
            </a>
          </div>

          <div v-if="pageData?.SA_Yazilar" class="guide-prose article-prose prose mt-10 max-w-none rounded-3xl border border-slate-200 bg-white p-6 text-slate-700 shadow-sm md:p-8" v-html="YaziRevised"></div>
        </div>

        <div class="umrah-guide-cta mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-10">
          <div class="relative overflow-hidden rounded-3xl bg-[#073c3f] px-6 py-8 text-white md:flex md:items-center md:justify-between md:px-10">
            <div class="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-amber-300/15 blur-3xl"></div><div class="relative max-w-2xl"><span class="text-xs font-extrabold uppercase tracking-[.18em] text-amber-300">Bilgiyi yolculuğa dönüştürün</span><h2 class="mt-2 text-white">Umre programlarını keşfedin</h2><p class="mt-3 text-sm leading-6 text-white/65">Konforlu konaklama, güçlü rehberlik ve özenle planlanmış programlarla kutsal topraklara huzurla yolculuk edin.</p></div><a :href="getPath('/tour/umre-turlari')" class="relative mt-6 inline-flex rounded-xl bg-amber-400 px-5 py-3 text-sm font-extrabold text-[#173f3d] hover:bg-amber-300 md:mt-0" @click.prevent="goPath('/tour/umre-turlari')">Umre turlarını görüntüle →</a>
          </div>
        </div>
        </section>
      </div>
    </template>
    <template v-else-if="isDeluxeUmrahConcept">
      <main class="bg-slate-50 pb-14">
        <section class="border-b border-teal-900/20 bg-gradient-to-br from-[#052f32] via-[#0b5557] to-[#173f3d] text-white">
          <div class="mx-auto max-w-screen-xl px-4 py-10 sm:px-6 md:py-14 lg:px-10">
            <nav class="mb-7 flex flex-wrap items-center gap-2 text-xs text-white/65" aria-label="Breadcrumb">
              <a :href="getPath('/')" class="font-semibold hover:text-amber-300" @click.prevent="goPath('/')">Ana Sayfa</a>
              <span aria-hidden="true">/</span>
              <a :href="getPath('/page/umre-rehberi')" class="font-semibold hover:text-amber-300" @click.prevent="goPath('/page/umre-rehberi')">Umre Rehberi</a>
              <span aria-hidden="true">/</span>
              <span class="font-bold text-white">Delüks Umre Konsepti</span>
            </nav>

            <div class="max-w-4xl">
              <p class="mb-3 text-xs font-extrabold uppercase tracking-[.18em] text-amber-300">Umre programı seçme rehberi</p>
              <h1 class="mb-4 text-3xl font-black leading-tight tracking-tight text-white md:text-5xl">{{ deluxeSeo.h1 }}</h1>
              <p class="mb-0 max-w-3xl text-base leading-8 text-white/80 md:text-lg">
                Delüks umre, tek başına resmî bir otel veya hizmet sınıfı değildir. Genellikle konaklama, ulaşım ve yolculuk desteğinin birlikte daha konforlu planlandığı programları anlatır; kesin karşılığı her turun açıklanan hizmetlerinde görülür.
              </p>
            </div>

            <div class="mt-8 grid max-w-5xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div v-for="fact in deluxeTrustFacts" :key="fact.label" class="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
                <strong class="block text-xl text-amber-300">{{ fact.value }}</strong>
                <span class="mt-1 block text-xs text-white/65">{{ fact.label }}</span>
              </div>
            </div>
          </div>
        </section>

        <div class="mx-auto max-w-screen-xl px-4 py-8 sm:px-6 lg:px-10">
          <nav class="mb-8 flex flex-wrap gap-2" aria-label="Sayfa içeriği">
            <a v-for="item in deluxeSectionLinks" :key="item.href" :href="item.href" class="rounded-full border border-teal-200 bg-white px-3 py-2 text-xs font-bold text-teal-800 hover:border-teal-500 hover:bg-teal-50">{{ item.label }}</a>
          </nav>

          <article class="grid gap-7 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div class="space-y-7">
              <section id="deluks-umre-nedir" class="scroll-mt-24 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-8" aria-labelledby="deluxe-definition-heading">
                <p class="mb-2 text-xs font-extrabold uppercase tracking-[.16em] text-teal-700">Temel tanım</p>
                <h2 id="deluxe-definition-heading" class="mb-4 text-2xl font-black text-slate-900 md:text-3xl">Delüks umre ne anlama gelir?</h2>
                <p class="mb-4 leading-7 text-slate-700">Delüks ifadesi çoğunlukla iyi konumlu oteller, düzenli transfer, kontrollü grup organizasyonu ve daha kapsamlı yolculuk desteği gibi unsurların bir arada sunulduğu programlar için kullanılır. Ancak sektörde bütün firmaları bağlayan tek bir “delüks umre” standardı bulunmaz.</p>
                <p class="mb-0 leading-7 text-slate-700">Bu nedenle yalnızca kategori adına güvenmek yerine otellerin tam adını ve Haremeyn’e mesafesini, uçuşları, oda yerleşimini, transfer modelini, yemekleri, rehberliği ve dahil olmayan hizmetleri yazılı olarak karşılaştırmak gerekir.</p>
              </section>

              <section id="kavram-farklari" class="scroll-mt-24 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm" aria-labelledby="deluxe-comparison-heading">
                <div class="border-b border-slate-200 p-5 md:p-8">
                  <p class="mb-2 text-xs font-extrabold uppercase tracking-[.16em] text-teal-700">Kavramları ayırın</p>
                  <h2 id="deluxe-comparison-heading" class="mb-3 text-2xl font-black text-slate-900 md:text-3xl">Delüks, lüks, 5 yıldızlı, VIP ve butik umre farkları</h2>
                  <p class="mb-0 leading-7 text-slate-600">Bu ifadeler aynı şeyi anlatmaz. Her biri programın farklı bir özelliğine vurgu yapabilir.</p>
                </div>
                <div class="overflow-x-auto">
                  <table class="w-full min-w-[700px] border-collapse text-left text-sm">
                    <thead class="bg-slate-900 text-white">
                      <tr>
                        <th class="px-5 py-4 font-bold">Kavram</th>
                        <th class="px-5 py-4 font-bold">Genellikle neyi anlatır?</th>
                        <th class="px-5 py-4 font-bold">Mutlaka doğrulanması gereken bilgi</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-200">
                      <tr v-for="row in deluxeComparisonRows" :key="row.term" class="align-top odd:bg-white even:bg-slate-50/70">
                        <th scope="row" class="px-5 py-4 font-extrabold text-teal-800">{{ row.term }}</th>
                        <td class="px-5 py-4 leading-6 text-slate-700">{{ row.meaning }}</td>
                        <td class="px-5 py-4 leading-6 text-slate-600">{{ row.verify }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="secim-listesi" class="scroll-mt-24 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-8" aria-labelledby="deluxe-checklist-heading">
                <p class="mb-2 text-xs font-extrabold uppercase tracking-[.16em] text-teal-700">Rezervasyon öncesi</p>
                <h2 id="deluxe-checklist-heading" class="mb-3 text-2xl font-black text-slate-900 md:text-3xl">Delüks umre programı seçerken kontrol listesi</h2>
                <p class="mb-5 leading-7 text-slate-600">Bir programı değerlendirirken aşağıdaki bilgilerin tur detayında veya yazılı teklifte açıkça bulunmasına dikkat edin.</p>
                <ul class="grid list-none gap-3 p-0 sm:grid-cols-2">
                  <li v-for="item in deluxeChecklist" :key="item" class="flex gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                    <span class="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-teal-700 text-xs font-black text-white" aria-hidden="true">✓</span>
                    <span>{{ item }}</span>
                  </li>
                </ul>
              </section>

              <section id="kimler-icin" class="scroll-mt-24 rounded-3xl border border-amber-200 bg-amber-50 p-5 md:p-8" aria-labelledby="deluxe-audience-heading">
                <h2 id="deluxe-audience-heading" class="mb-4 text-2xl font-black text-slate-900">Delüks umre kimler için uygun olabilir?</h2>
                <p class="mb-4 leading-7 text-slate-700">Otel konumuna, düzenli ulaşıma ve yolculuk boyunca açıklanmış bir destek planına önem veren misafirler için uygun olabilir. İleri yaştaki yolcular, çocuklu aileler veya ilk kez umreye gidecek kişiler açısından kısa yürüme mesafesi ve operasyon düzeni özellikle önemli olabilir.</p>
                <p class="mb-0 leading-7 text-slate-700">Yine de en doğru seçim “delüks” etiketinden değil, kişinin sağlık durumu, bütçesi, seyahat süresi ve hizmet beklentisiyle programın gerçek kapsamının eşleşmesinden doğar.</p>
              </section>

              <section id="sik-sorulanlar" class="scroll-mt-24 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-8" aria-labelledby="deluxe-faq-heading">
                <p class="mb-2 text-xs font-extrabold uppercase tracking-[.16em] text-teal-700">Merak edilenler</p>
                <h2 id="deluxe-faq-heading" class="mb-5 text-2xl font-black text-slate-900 md:text-3xl">Delüks umre hakkında sık sorulan sorular</h2>
                <div class="space-y-3">
                  <details v-for="item in deluxeFaqs" :key="item.question" class="group rounded-2xl border border-slate-200 bg-slate-50 p-4 open:bg-white">
                    <summary class="cursor-pointer list-none pr-8 font-bold leading-6 text-slate-900">{{ item.question }}</summary>
                    <p class="mb-0 mt-3 border-t border-slate-200 pt-3 text-sm leading-7 text-slate-600">{{ item.answer }}</p>
                  </details>
                </div>
              </section>
            </div>

            <aside>
              <div class="sticky top-24 space-y-5">
                <div class="rounded-3xl bg-slate-900 p-5 text-white shadow-lg">
                  <p class="mb-2 text-xs font-extrabold uppercase tracking-[.15em] text-amber-300">Programları karşılaştırın</p>
                  <h2 class="mb-3 text-xl font-black text-white">Gerçek tur detaylarını inceleyin</h2>
                  <p class="mb-5 text-sm leading-6 text-white/70">Otel, tarih, süre, uçuş, oda ve dahil hizmet bilgilerini güncel tur sayfalarında karşılaştırın.</p>
                  <a :href="getPath('/tour/5-yildizli-luks-umre-turlari')" class="mb-3 block rounded-xl bg-amber-400 px-4 py-3 text-center text-sm font-extrabold text-slate-900 hover:bg-amber-300" @click.prevent="goPath('/tour/5-yildizli-luks-umre-turlari')">5 yıldızlı lüks umre</a>
                  <a :href="getPath('/tour/vip-umre-programlari')" class="block rounded-xl border border-white/20 px-4 py-3 text-center text-sm font-bold text-white hover:bg-white/10" @click.prevent="goPath('/tour/vip-umre-programlari')">VIP ve butik umre</a>
                </div>
                <div class="rounded-3xl border border-teal-200 bg-teal-50 p-5">
                  <h2 class="mb-2 text-lg font-black text-slate-900">Kişisel bir plan mı arıyorsunuz?</h2>
                  <p class="mb-4 text-sm leading-6 text-slate-600">Aileniz veya özel grubunuz için tarih, otel, uçuş ve hizmet kapsamı belirlenmiş bir program talep edebilirsiniz.</p>
                  <a :href="getPath('/tour/kisiye-ozel-umre-turlari')" class="font-extrabold text-teal-800 hover:underline" @click.prevent="goPath('/tour/kisiye-ozel-umre-turlari')">Kişiye özel umreyi inceleyin →</a>
                </div>
              </div>
            </aside>
          </article>
        </div>
      </main>
    </template>
    <template v-else-if="isJourneyHub">
      <JourneyHub />
    </template>
    <template v-else-if="isFaqPage">
      <FaqPage />
    </template>
    <template v-else>
      <div
        :class="[isGuideInnerPage ? 'guide-inner-page bg-[#f5f6f2] pb-12' : '', isPracticalGuideInnerPage ? 'umrah-practical-inner' : '']"
        :style="isGuideInnerPage ? { '--guide-header-image': `url('${isPracticalGuideInnerPage ? '/assets/img/guides/umre-rehberi-hero.webp' : '/assets/img/guides/umre-hac-rehberi-hero.webp'}')` } : undefined"
      >
        <div class="guide-page-header w-full py-6 mb-4 px-4 md:px-8 bg-gradient-to-r from-neutral-100 via-white to-brand-50 text-page border-y border-neutral-200">
          <div class="mx-auto container">
            <div v-if="isGuideInnerPage" class="mb-2 inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[.16em] text-amber-200">
              <font-awesome-icon icon="fa-solid fa-kaaba" class="h-3 w-3" /> {{ $t(innerGuideLabel) }}
            </div>
            <h1 v-if="pageData && pageData.SA_Baslik" class="text-2xl md:text-4xl leading-tight font-semibold text-neutral-900 mb-0 mt-2" v-html="pageData.SA_Baslik"></h1>
          </div>
        </div>
        <div class="guide-breadcrumb container mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-2 rounded-2xl border border-neutral-200 bg-white px-3 md:px-4 py-2.5 mb-3">
            <nav class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500" aria-label="Breadcrumb">
              <template v-for="(br, index) in breadcrumb" :key="br.text + (br.link || '')">
                <a v-if="br.link" :href="getPath(br.link)" class="font-semibold transition hover:text-teal-700" @click.prevent.stop="goPath(br.link)">{{ br.text }}</a>
                <span v-else class="max-w-full truncate font-bold text-slate-800">{{ br.text }}</span>
                <span v-if="index < breadcrumb.length - 1" class="text-slate-300">/</span>
              </template>
            </nav>
            <GlobalShare
                :title="`${$t('Seninle bunu paylaşıyorum')} ${pageData.SA_Baslik} ${url}`"
                :url="url"
            ></GlobalShare> 
        </div>
        <div class="container mx-auto px-0">
          <div class="py-2 xl:pt-4 xl:pb-0 grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-8">
          <div class="w-full text-page block mb-10 lg:mb-16 lg:col-span-8">
            <div class="guide-article-card w-full block mb-8 rounded-2xl border border-neutral-200 bg-white p-4 md:p-6 shadow-[0_18px_34px_-28px_rgba(22,32,51,0.4)]">
             
              <template v-if="pageData?.SA_Sound">
                <audio controls class="w-full mb-4"  preload="none">
                  <source :src="pageData.SA_Sound" type="audio/mpeg">
                  Your browser does not support the audio element.
                </audio>
              </template>
              <template v-if="pageData?.SA_Fotograf">
                <img v-if="isMobile" :src="showImage(pageData.SA_Fotograf, 128, 128, true, 90)" class="max-h-48 rounded-2xl float-left mr-5 mb-4" 
                  style="width: 128px; height:128px;"
                  :alt="pageData.SA_Baslik"
                />
                <img v-else-if="!isMobile" :src="showImage(pageData.SA_Fotograf, 264, 264, true, 90)" class="max-h-48 md:max-h-80 rounded-2xl md:float-left mr-6 mb-4" 
                  style="width: 264px; height:264px;"
                  :alt="pageData.SA_Baslik"
                />
              </template>
              <span v-if="pageData && pageData.SA_Yazilar" class="article-prose prose max-w-none text-base text-neutral-700" v-html="YaziRevised"></span>
            </div>
            <template v-if="parentPages['parent'] && parentPages['parent'].length > 0">
                <div    v-if="pageStyle=='standard'"
                  :class="['grid gap-3 grid-col-1 mb-5', !pageData?.SA_Fotograf ?  'md:grid-cols-2 lg:grid-cols-2 xl-grid-cols-2': 'md:grid-cols-2 lg:grid-cols-2 xl-grid-cols-2']">
                  
                  <template  v-for="p in parentPages['parent']">
                    <a 
                      @click.prevent.stop="goPath('/page/'+p.SA_Link)"
                      v-if="p.SA_Link!='#'"
                      :href="getPath('/page/'+p.SA_Link)"
                      class="guide-related-card p-3.5 rounded-2xl border border-neutral-200 bg-white flex shadow-sm hover:shadow-md hover:border-brand-300 hover:bg-neutral-50 transition-all duration-300">
                      <template v-if="p.SA_Fotograf">
                        <img :src="showImage(p.SA_Fotograf, 64, 64, true, 90)" class="max-h-80 md:float-left mr-4 mb-2 rounded-xl" 
                        :alt="p.SA_Baslik"
                          style="width:64px;height:64px;"
                        />
                      </template>
                      <img v-else src="/assets/img/logo/logo.png" style="width: 64px; height:fit" class="max-h-80 h-fit flex h-auto md:float-left mr-4 mb-2 rounded-xl" />

                        <span class="flex flex-col">
                        <span v-html="p.SA_Baslik" class="block text-md font-semibold text-neutral-900 mb-1"></span>
                        <span class="block text-sm text-neutral-600" v-html="p.SA_Subtitle"></span>
                        </span>
                      
                      </a>
                  </template>

                  <div>

                    <span class="p-1.5 flex text-left" v-if="!parentPages['parent'] || parentPages['parent'].length==0">
                        {{ $t('Son güncelleme') }}: {{ pageData.updated_on ? formatDate(pageData.updated_on,"dd.MM.yyyy") : '' }}
                    </span>
                </div>
                </div>
                <div    v-if="pageStyle=='split'"   class="grid gap-3 md:grid-cols-2 mb-5"> 
                  <div  v-for="p in parentPages['parent']">
                    <a 
                      @click.prevent.stop="goPath('/page/'+p.SA_Link)"
                      v-if="p.SA_Link!='#'"
                      :href="getPath('/page/'+p.SA_Link)"
                      class="px-3 py-2 rounded-xl border border-neutral-200 flex shadow-sm hover:shadow-md hover:border-support-300 hover:bg-neutral-50 transition-all duration-300">
                     
                        <span v-html="p.SA_Baslik" class="block text-md font-semibold"></span> 
                      
                      </a>
 
                      <ul  class="list-none p-0 m-0" style="margin-left: 0px;">
                        <li v-for="item in subPages[p.pdb_Id]" :key="item.id"  class="block text-sm my-1.5 cursor-pointer hover:text-sky-800 hover:underline">
                          <a @click.prevent.stop="goPath('/page/'+item.SA_Link)" :href="getPath('/page/'+item.SA_Link)">
                            <font-awesome-icon icon="fa-solid fa-chevron-right" class="w-3 h-3 mr-2 text-sky-800"></font-awesome-icon>  
                            <span v-html="item.SA_Baslik"></span>
                          </a>
                        </li>
                      </ul>
                  </div>

                  <div>

                    
                </div>
                </div>
            </template>
          </div> 
        

          <aside v-if="(parentPages['similar'] && parentPages['similar'].length > 0) || sidebarLoading" class="flex-none w-full lg:col-span-4 lg:pl-0">
            <div class="rounded-2xl border border-neutral-200 bg-white p-4 md:p-5 lg:sticky lg:top-24 shadow-[0_18px_34px_-28px_rgba(22,32,51,0.35)]">
            <div v-if="sidebarLoading" class="py-1" role="status" aria-live="polite">
              <div class="flex items-center gap-3 border-b border-slate-100 pb-4">
                <span class="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                  <font-awesome-icon icon="fa-solid fa-spinner" class="h-4 w-4 animate-spin" />
                </span>
                <div>
                  <p class="m-0 text-sm font-extrabold text-slate-800">{{ locale === 'en' ? 'Loading related pages' : 'Diğer sayfalar yükleniyor' }}</p>
                  <p class="m-0 mt-0.5 text-[11px] text-slate-400">{{ locale === 'en' ? 'Preparing guide topics…' : 'Rehber başlıkları hazırlanıyor…' }}</p>
                </div>
              </div>
              <div class="mt-4 space-y-3">
                <div v-for="item in 5" :key="`sidebar-loading-${item}`" class="flex animate-pulse items-center gap-3">
                  <span class="h-7 w-7 rounded-lg bg-slate-100"></span>
                  <span class="h-2.5 rounded-full bg-slate-100" :class="item % 2 ? 'w-2/3' : 'w-4/5'"></span>
                </div>
              </div>
              <span class="sr-only">{{ locale === 'en' ? 'Loading' : 'Yükleniyor' }}</span>
            </div>
            <template v-else>
            <GlobalButton 
              v-if="nextPath" 
              @click="goPath(nextPath)"
              py="3"
              size="lg"
              variant="primary"
              class="w-full mb-3"
              icon-class="mr-2"
              icon="fa-solid fa-chevron-right"
              :label="$t('Sonraki Yazı')"
            ></GlobalButton>
            <p class="text-md font-semibold text-neutral-900 my-1">{{$t('Diğer Başlıklar')}}</p>
            <ul class="block w-full">
              <template v-for="item in parentPages['similar']" >
              <li class="block text-sm my-1.5 cursor-pointer" v-if="item.SA_Link!='#'"><a
                :class="item.pdb_Id == pageData.wa_sayfa_Id ? 'text-sky-700 underline  font-semibold' : ''"
                @click.prevent.stop="goPath('/page/'+item.SA_Link)"
                :href="getPath('/page/'+item.SA_Link)"
                class="inline-flex items-start hover:text-sky-800 hover:underline"
              >
                  <font-awesome-icon icon="fa-solid fa-chevron-right" class="w-3 h-3 mr-2 text-sky-800"></font-awesome-icon>
                  <span v-html="item.SA_Baslik"></span>  
                  </a>
              </li>
            </template>
            </ul>
            <div v-if="pageData?.SA_UstSayfa == 'c3RhYmlsUGFnZXN0cjU2MDExNTI5MjczODM2'">
                <span class="bg-neutral-100 rounded-xl p-2 mt-4 block">
                  <a  :href="getPath('/page/umre-ve-hac-rehberi')" @click.prevent.stop="goPath('/page/umre-ve-hac-rehberi')"   class="text-md text-amber-600  hover:underline">
                    <font-awesome-icon icon="fa-solid fa-chevron-right" class="w-4 h-4 mr-2"></font-awesome-icon>
                    {{ $t('Umre Manevi Rehberi') }}
                  </a>
                </span>
            </div>
            <div v-if="pageData?.SA_UstSayfa == 'QmxvZ0thdGVnb3JpbGVydHIyNjUxNzQ5NDkyMjg0'">
                <span class="bg-neutral-100 rounded-xl p-2 mt-4 block">
                  <a  :href="getPath('/page/umre-rehberi')" @click.prevent.stop="goPath('/page/umre-rehberi')"   class="text-md text-amber-600  hover:underline">
                    <font-awesome-icon icon="fa-solid fa-chevron-right" class="w-4 h-4 mr-2"></font-awesome-icon>
                    {{ $t('Umre Rehberi') }}
                  </a>
                </span>
            </div> 

       
            <div v-if="otherPages && otherPages.length > 0">
               
 
                <ul class="block w-full border-t border-neutral-200 mt-2 pt-3" >
                    <template v-for="item in otherPages" >
                    <li class="block text-sm my-2 cursor-pointer" v-if="item.SA_Link!='#'">
                      <a
                      :class="item.pdb_Id == pageData.SA_UstSayfa ? 'text-sky-700 underline  font-semibold' : ''" 
                      @click.prevent.stop="activeSubPageId = item.pdb_Id"
                      class="flex items-center"
                    >
                        <font-awesome-icon icon="fa-solid fa-chevron-right" class="w-3 h-3 mr-2 text-sky-800"></font-awesome-icon>
                        <span v-html="item.SA_Baslik"></span> 
                        <span class="text-slate-600 px-1 rounded-full bg-slate-100 block ml-2 text-xs"> {{ subPages[item.pdb_Id]? subPages[item.pdb_Id].length + ` yazı` : '' }}</span>
                        </a>
                        <ul v-show="item.pdb_Id == activeSubPageId" class="list-none p-0 m-0 W-FULL overflow-hidden" style="margin-left: 15px;">
                          <li v-for="subItem in subPages[item.pdb_Id]" :key="subItem.id" class="text-sm my-2 cursor-pointer hover:text-sky-800 hover:underline">
                            <a @click.prevent.stop="goPath('/page/'+subItem.SA_Link)" 
                            :href="getPath('/page/'+subItem.SA_Link)" class="truncate"
                             :class="subItem.pdb_Id == pageData.wa_sayfa_Id ? 'text-sky-700 underline  font-semibold' : ''"
                             >
                              <font-awesome-icon v-if="subItem.pdb_Id == pageData.wa_sayfa_Id" icon="fa-solid fa-dot-circle" class="w-3 h-3 mr-2 text-sky-800"></font-awesome-icon>  
                              <font-awesome-icon v-if="subItem.pdb_Id != pageData.wa_sayfa_Id"icon="fa-solid fa-circle" class="w-3 h-3 mr-2 text-slate-300"></font-awesome-icon>  
                              <span v-html="subItem.SA_Baslik"></span>  
                            </a>
                          </li>
                        </ul>
                    </li>
                  </template>
                  </ul>
            </div> 
            </template>

            </div>
          </aside>

   
       
        </div>
      </div>
      </div>
      </template>
      <JourneyPageLinks v-if="isGuideLanding || isUmrahGuideLanding" :current="isGuideLanding ? 'spiritual' : 'practical'" />
  </div>
</template>
<style scoped>
.article-prose :deep(p) {
  margin: 0 0 0.75rem;
  font-size: 1rem;
  line-height: 1.7;
}

.article-prose :deep(h2) {
  color: #111827;
  margin: 1.5rem 0 0.6rem;
  font-size: 1.65rem;
  line-height: 1.25;
}

.article-prose :deep(h3) {
  color: #111827;
  margin: 1.25rem 0 0.5rem;
  font-size: 1.3rem;
  line-height: 1.3;
}

.article-prose :deep(h4),
.article-prose :deep(h5),
.article-prose :deep(h6) {
  color: #111827;
  margin: 1rem 0 0.4rem;
  font-size: 1.1rem;
  line-height: 1.35;
}

.article-prose :deep(ul),
.article-prose :deep(ol) {
  margin: 0.75rem 0;
  padding-left: 1rem;
}

.article-prose :deep(img) {
  border-radius: 0.9rem;
  margin: 1rem 0;
}

.guide-landing section {
  margin: 0;
}

.guide-landing section:first-child {
  padding-top: 0;
  padding-bottom: 0;
}

.guide-landing #guide-topics {
  padding-top: 3.5rem;
  padding-bottom: 3.5rem;
}

.umrah-guide-landing .umrah-guide-shortcuts {
  padding-top: 0;
  padding-bottom: 0;
}

.umrah-guide-landing .umrah-guide-content {
  padding-top: 4.5rem;
  padding-bottom: 3.5rem;
}

.umrah-guide-landing .umrah-guide-cta {
  padding-top: 0;
  padding-bottom: 0;
}

.guide-landing p {
  margin-bottom: 0;
}

.guide-landing h1 {
  margin: 0;
  font-size: clamp(2.25rem, 5vw, 3.75rem);
  line-height: 1.08;
}

.guide-landing h2 {
  margin: 0;
  font-size: clamp(1.8rem, 3vw, 2.35rem);
  line-height: 1.16;
}

.guide-landing h3 {
  margin: 0;
  font-size: 1.2rem;
  line-height: 1.28;
}

.guide-inner-page .guide-page-header {
  position: relative;
  isolation: isolate;
  margin-bottom: 0.75rem;
  padding-top: 2.25rem;
  padding-bottom: 2.25rem;
  overflow: hidden;
  border-color: rgba(245, 195, 70, 0.25);
  background-image: linear-gradient(90deg, rgba(4, 43, 47, .98) 0%, rgba(5, 56, 59, .92) 52%, rgba(5, 56, 59, .54) 100%), var(--guide-header-image);
  background-position: center, 70% 52%;
  background-size: cover;
}

.guide-inner-page .guide-page-header h1 {
  margin: 0;
  max-width: 820px;
  color: #fff;
  font-size: clamp(1.75rem, 4vw, 2.75rem);
  line-height: 1.15;
}

.guide-inner-page.umrah-practical-inner .guide-page-header {
  background-image: linear-gradient(90deg, rgba(4, 43, 47, .98) 0%, rgba(5, 56, 59, .9) 55%, rgba(5, 56, 59, .45) 100%), var(--guide-header-image);
}

.guide-inner-page .guide-breadcrumb {
  border-color: #dce8e6;
  box-shadow: 0 12px 32px -28px rgba(6, 78, 76, .55);
}

.guide-inner-page .guide-article-card {
  border-color: #dce8e6;
  border-top: 3px solid #0f766e;
  border-radius: 1.5rem;
  padding: 1.25rem;
}

.guide-inner-page .guide-related-card {
  border-color: #dce8e6;
}

.guide-inner-page .guide-related-card:hover {
  border-color: #5eead4;
  box-shadow: 0 18px 40px -32px rgba(6, 95, 70, .55);
}

.guide-inner-page p {
  margin-bottom: 0;
}

.guide-inner-page h1,
.guide-inner-page h2,
.guide-inner-page h3,
.guide-inner-page h4,
.guide-inner-page h5,
.guide-inner-page h6 {
  line-height: 1.25;
}

@media (min-width: 768px) {
  .umrah-guide-landing .umrah-guide-content {
    padding-top: 2rem;
    padding-bottom: 1.5rem;
  }

  .guide-inner-page .guide-article-card {
    padding: 2rem;
  }
}

.guide-grid {
  background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,.22) 1px, transparent 0);
  background-size: 26px 26px;
  mask-image: linear-gradient(to right, black, transparent 72%);
}

.guide-grid-right {
  background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,.2) 1px, transparent 0);
  background-size: 26px 26px;
  mask-image: linear-gradient(to left, black, transparent 72%);
}

.guide-prose :deep(h2),
.guide-prose :deep(h3) {
  color: #073c3f;
  font-weight: 800;
}

.guide-prose :deep(a) {
  color: #0f766e;
  font-weight: 700;
}
</style>
<script setup>
import { ref } from 'vue';
import axios from "axios"
import { useCommon } from "~/composables/useCommon";
import inancComponent from "~/components/page/inanc.client.vue"; 
import FaqPage from "~/components/page/FaqPage.vue";
import JourneyHub from "~/components/page/JourneyHub.vue";
import JourneyPageLinks from "~/components/page/JourneyPageLinks.vue";
import { buildUmrahSeoStrategy } from '~/utils/umrahSeoStrategy';
import { guides } from '~/data/guides';
import { sub } from 'date-fns';
const { goPath, getPath, decodeHTMLEntities, showImage, formatDate } = useCommon();
const pageData = usePageData();
const route = useRoute()
const specialPageLinks = ['hakkimizda', 'aboutus', 'yorumlar', 'comments', 'umre-tur-fotograflari', 'umrah-tour-photos', 'belgeler', 'documents', 'videolar', 'videos', 'ekibimiz', 'our-team', 'photos']
const isSpecialPage = computed(() => specialPageLinks.includes(route.params?.link))
const specialPageSeo = computed(() => {
  const isEnglish = locale.value === 'en'
  const content = {
    hakkimizda: ['İnanç Turizm Hakkında | Hac, Umre ve Seyahat Hizmetleri', 'İnanç Turizm’in hizmet yaklaşımını, ekibini, yetki belgelerini ve misafir deneyimlerini inceleyin.'],
    aboutus: ['About Inanç Turizm | Hajj, Umrah and Travel Services', 'Explore Inanç Turizm’s service approach, team, authorisation documents and traveller experiences.'],
    yorumlar: ['İnanç Turizm Misafir Yorumları | Umre ve Hac Deneyimleri', 'İnanç Turizm misafirlerinin umre ve hac yolculuk deneyimlerini, kaynak ve tarih bilgileriyle inceleyin.'],
    comments: ['Inanç Turizm Traveller Reviews | Umrah and Hajj Experiences', 'Explore Umrah and Hajj traveller experiences together with their publication sources and dates.'],
    belgeler: ['İnanç Turizm Yetki ve Üyelik Belgeleri', 'İnanç Turizm’in hac, umre, seyahat acentası, Diyanet, IATA ve TÜRSAB belgelerini inceleyin.'],
    documents: ['Inanç Turizm Authorisation and Membership Documents', 'Review Inanç Turizm’s Hajj, Umrah, travel agency, Diyanet, IATA and TÜRSAB documents.'],
    'umre-tur-fotograflari': ['İnanç Turizm Umre Tur Fotoğrafları', 'İnanç Turizm umre gruplarının yolculuklarından fotoğrafları inceleyin.'],
    'umrah-tour-photos': ['Inanç Turizm Umrah Tour Photos', 'Browse photos shared from Inanç Turizm Umrah group journeys.'],
    ekibimiz: ['İnanç Turizm Ekibi', 'İnanç Turizm’in Türkiye ve Suudi Arabistan ekiplerini ve uzmanlık alanlarını tanıyın.'],
    'our-team': ['Inanç Turizm Team', 'Meet Inanç Turizm’s teams in Türkiye and Saudi Arabia and their areas of expertise.']
  }
  const fallback = isEnglish
    ? ['Inanç Turizm | Hajj, Umrah and Travel Services', 'Explore Inanç Turizm’s travel services, team and company information.']
    : ['İnanç Turizm | Hac, Umre ve Seyahat Hizmetleri', 'İnanç Turizm’in seyahat hizmetlerini, ekibini ve kurum bilgilerini inceleyin.']
  const [title, description] = content[route.params?.link] || fallback
  return { title, description }
})
const isDeluxeUmrahConcept = computed(() => route.path === '/page/delux-umre-konsepti')
const config = useRuntimeConfig();
const isMobile = useIsMobile();
const contactForm = useContactForm();
const {locale,t} = useI18n()
const error = useError() 
const loaded = ref(false)
const sidebarLoading = ref(true)
const pageStyle = ref('standard'); // or 'split'
const activeSubPageId = ref(null);
const updateImageSrcs = (htmlString)=>{
  return htmlString.replace(/<img[^>]+src=["']([^"']+)["']/gi, (match, src) => {
    const newSrc = !isMobile.value ? showImage(src, 1024, null, true, 90) : showImage(src, 480, null, true, 90);
    return match.replace(src, newSrc);
  });
}
const YaziRevised = computed(() => {
  let yazi = pageData.value.SA_Yazilar;
  if(yazi){
    
    yazi = decodeHTMLEntities(yazi,pageData.value);
    yazi = updateImageSrcs(yazi);
    yazi = yazi.replace(/<img[^>]+>/g, function(img) {
      return img.replace(/width="[^"]+"/g, 'width="100%"').replace(/height="[^"]+"/g, 'height="auto"');
    });
    yazi = yazi.replace(/<p[^>]+>/g, '<p class="text-md text-slate-700">');
  }
  return yazi;
})
 
if(pageData.value){
 
      let useHeadData = {
        link:[]
      };
      let SA_Link =  pageData.value.SA_Urls?.[locale.value] ? pageData.value.SA_Urls?.[locale.value] : pageData.value.SA_Link;
      if(pageData.value.SA_Fotograf){ 
        useHeadData.link.push({ rel: "preload", as: "image", href: showImage(pageData.value.SA_Fotograf, 264, 264, true, 90) }); 
      } 
      if(pageData.value?.SA_Urls && pageData.value.SA_Urls?.[locale.value]){
        useHeadData.link.push({ rel: "alternate", hreflang: "x-default", href: "https://inanctur.com.tr/page/"+pageData.value.SA_Urls?.["tr"] });  
        useHeadData.link.push({ rel: "alternate", hreflang: "tr", href: "https://inanctur.com.tr/page/"+pageData.value.SA_Urls?.["tr"] });  
        if(pageData.value.SA_Urls?.["en"] && pageData.value.SA_Urls?.["en"]!="#"){
          useHeadData.link.push({ rel: "alternate", hreflang: "en", href: "https://inanctur.com.tr/en/page/"+pageData.value.SA_Urls?.["en"] }); 
        }
      }
       
      if(pageData.value.created_on && !isDeluxeUmrahConcept.value && !isSpecialPage.value){
      
          const jsonLdData = {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "headline": pageData.value.SA_Baslik,
            "image": [
              showImage(pageData.value.SA_Fotograf, 264, 264, true, 90) 
            ],
            "datePublished": formatDate(pageData.value.created_on,"yyyy-MM-dd'T'HH:mm:ssXXX"),
            "dateModified": formatDate(pageData.value.updated_on,"yyyy-MM-dd'T'HH:mm:ssXXX"),
            "url": locale.value == "tr" ?  "https://inanctur.com.tr/page/"+SA_Link : "https://inanctur.com.tr/en/page/"+SA_Link,
            "author": [
              {
                "@type": "Person",
                "name": "Fatih Tozatan", 
              }
            ]
          }; 
          useHeadData.script = [
            {
              type: 'application/ld+json',
              children: JSON.stringify(jsonLdData)
            }
          ]
          
       
          useHead(useHeadData) 
      }else if (pageData.value?.notFound && !isDeluxeUmrahConcept.value && !isSpecialPage.value && !['faq', 'frequently-asked-questions', 'yolculuk-merkezi', 'journey-centre'].includes(route.params?.link)){
        error.value = { statusCode: 404, statusMessage: 'Page Not Found' }
      }
}
const parentPages = ref({
  similar:[],
  parent:[]
})


const otherPages = ref([]);
const getGuide = async (parentId="QmxvZ0thdGVnb3JpbGVydHIyNjUxNzQ5NDkyMjg0") => {
  loaded.value = false; 
  otherPages.value = [];
  let slang = locale.value ? locale.value:  "tr";
  let query = `{
    pageDetails(SA_UstSayfa: "` + parentId + `", order: "pdb_sira", pdb_status: 1, wa_lang: "` + slang + `") {
        pdb_language 
        SA_Yazilar
        SA_Fotograf
        SA_Baslik
        SA_Subtitle
        pdb_categories
        SA_UstSayfa
        SA_Link
        pdb_Id
    }
          } 
        `;
 
  await axios({
    url: `${config.public.apiBase}`+"/graphql/direct",
    method: 'post',
    data: {
      query
    }
  }).then(response => {

    otherPages.value = response.data.data.pageDetails;
 

  }).catch(e => {

    console.log(e)
    loaded.value = true;
  }) 
}

const subPages = ref({});
const AllSubPages = ref([]);
const getSubPages = async (parentId) => {

   let slang = locale.value ? locale.value:  "tr";
  let query = `{
    pageDetails(pdb_categories: "` + parentId + `", order: "pdb_sira", pdb_status: 1, wa_lang: "` + slang + `") {
        pdb_language 
        SA_Yazilar
        SA_Fotograf
        SA_Baslik
        SA_Subtitle
        pdb_categories
        SA_UstSayfa
        SA_Link
        pdb_Id
    }
          } 
        `;
 
  await axios({
    url: `${config.public.apiBase}`+"/graphql/direct",
    method: 'post',
    data: {
      query
    }
  }).then(response => {
    

    response.data.data.pageDetails?.forEach(element => {
    if(!subPages.value[element.SA_UstSayfa]){
     subPages.value[element.SA_UstSayfa] = [];
    }
    element.SA_Fotograf = showImage(element.SA_Fotograf, 264, 264, true, 90);
    subPages.value[element.SA_UstSayfa].push(element);
    });

         otherPages.value.forEach((item) => {
       
          subPages.value[item.pdb_Id].forEach((subItem) => {
            AllSubPages.value.push(subItem);
          });
        });
        response.data.data.pageDetails;

  }).catch(e => {

    console.log(e)
    loaded.value = true;
  }) 
}


const getParent = async (type="parent") => {
  loaded.value = false; 
  if (type === 'similar') sidebarLoading.value = true;
  let parent =  type == "similar" ?   pageData.value.SA_UstSayfa : pageData.value.wa_sayfa_Id;
  if((type == "parent" ) || (type == "similar" && parent)){
  let slang = locale.value ? locale.value:  "tr";
  let query = `{
    pageDetails(SA_UstSayfa: "` + parent + `", order: "pdb_sira", pdb_status: 1, wa_lang: "` + slang + `") {
        pdb_language 
        SA_Yazilar
        SA_Fotograf
        SA_Baslik
        SA_Subtitle
        pdb_categories
        SA_UstSayfa
        SA_Link
        pdb_Id
    }
          } 
        `;
 
  await axios({
    url: `${config.public.apiBase}`+"/graphql/direct",
    method: 'post',
    data: {
      query
    }
  }).then(response => {

    parentPages.value[type] = response.data.data.pageDetails;
    activeSubPageId.value = parent;

    if(type=="similar"){
      setTimeout(() => {
        loaded.value = true;
        sidebarLoading.value = false;
      },100);
    }

  }).catch(e => {

    console.log(e)
    loaded.value = true;
    if (type === 'similar') sidebarLoading.value = false;
  })
}else{
  parentPages.value[type] = [
    
  ]
  loaded.value = true;
  if (type === 'similar') sidebarLoading.value = false;
}
}

const url = ref('');
const isGuideLanding = computed(() => ['umre-ve-hac-rehberi', 'umrah-and-hajj-guide'].includes(route.params?.link))
const isUmrahGuideLanding = computed(() => ['umre-rehberi', 'umrah-guide'].includes(route.params?.link))
const isFaqPage = computed(() => ['faq', 'frequently-asked-questions'].includes(route.params?.link))
const isJourneyHub = computed(() => ['yolculuk-merkezi', 'journey-centre'].includes(route.params?.link))
const deluxeSeo = computed(() => buildUmrahSeoStrategy(new Date().getFullYear(), 'tr').deluxeGuide)
const deluxeTrustFacts = [
  { value: '21 yıl', label: 'Umre organizasyonu deneyimi' },
  { value: '3.500', label: 'Yıllık umre yolcusu' },
  { value: '40.000+', label: 'Bugüne kadar umre yolcusu' },
  { value: '7677', label: 'TÜRSAB belge numarası' }
]
const deluxeSectionLinks = [
  { label: 'Delüks umre nedir?', href: '#deluks-umre-nedir' },
  { label: 'Kavramların farkı', href: '#kavram-farklari' },
  { label: 'Seçim kontrol listesi', href: '#secim-listesi' },
  { label: 'Kimler için uygun?', href: '#kimler-icin' },
  { label: 'Sık sorulan sorular', href: '#sik-sorulanlar' }
]
const deluxeComparisonRows = [
  {
    term: '5 yıldızlı umre',
    meaning: 'Öncelikle konaklama tesisinin resmî yıldız kategorisini ifade eder.',
    verify: 'Otelin tam adı, şehirdeki konumu, Haremeyn’e mesafesi, gece ve yemek planı.'
  },
  {
    term: 'Lüks umre',
    meaning: 'Konaklama, ulaşım ve hizmet konforunun genel düzeyine vurgu yapan ticari bir tanımdır.',
    verify: 'Yıldız sınıfının ötesinde uçuş, transfer, oda, yemek ve rehberlik kapsamı.'
  },
  {
    term: 'VIP umre',
    meaning: 'Daha kişisel hizmet, özel transfer veya esnek planlama beklentisini anlatabilir.',
    verify: 'Hangi transferlerin özel olduğu, grup büyüklüğü ve kişisel desteğin sınırları.'
  },
  {
    term: 'Butik umre',
    meaning: 'Çoğunlukla daha küçük ve kontrollü bir grup yapısını öne çıkarır.',
    verify: 'Planlanan ve azami katılımcı sayısı, rehber başına yolcu sayısı ve seyahat temposu.'
  },
  {
    term: 'Delüks umre',
    meaning: 'Konforlu konaklama, ulaşım ve desteğin paket halinde sunulduğunu anlatan geniş bir kavramdır.',
    verify: 'Programda vaat edilen her hizmetin adı, kapsamı, dahil veya hariç oluşu.'
  }
]
const deluxeChecklist = [
  'Mekke ve Medine otellerinin tam adı, yıldız sınıfı ve Haremeyn’e gerçek ulaşım biçimi',
  'Her şehirdeki gece sayısı, oda tipi, kişi yerleşimi ve yemek planı',
  'Havayolu, uçuş sınıfı, aktarma bilgisi ve bagaj hakkı',
  'Havalimanı, Mekke–Medine ve ziyaret transferlerinin araç ve paylaşım düzeni',
  'Grubun planlanan büyüklüğü ve rehberlik hizmetinin nasıl sunulacağı',
  'Vize, sigorta, ziyaretler ve organizasyon hizmetlerinden hangilerinin dahil olduğu',
  'Çocuk, bebek, tek kişilik oda ve özel talepler için uygulanacak koşullar',
  'Ödeme, iptal, değişiklik ve iade şartlarının yazılı olarak açıklanması'
]
const deluxeFaqs = [
  {
    question: 'Delüks umre nedir?',
    answer: 'Delüks umre; konaklama, ulaşım ve yolculuk desteğinin daha konforlu bir kapsamda birleştirildiğini anlatan ticari bir program tanımıdır. Tek bir resmî standardı olmadığı için turun yazılı hizmet içeriği esas alınmalıdır.'
  },
  {
    question: 'Delüks umre ile 5 yıldızlı umre aynı şey midir?',
    answer: 'Hayır. 5 yıldız ifadesi öncelikle otelin sınıfını gösterir. Delüks ise otel dışında uçuş, transfer, yemek, grup yapısı ve rehberlik gibi hizmetlerin bütününü tanımlamak için kullanılabilir.'
  },
  {
    question: 'Delüks umre ile VIP umre arasındaki fark nedir?',
    answer: 'Delüks umre genel paket konforunu, VIP umre ise çoğunlukla daha kişisel hizmet veya özel transfer beklentisini öne çıkarır. Uygulama firmaya ve kalkışa göre değişebileceğinden kapsam yazılı olarak teyit edilmelidir.'
  },
  {
    question: 'Delüks umre her zaman Kâbe’ye yakın otelde mi yapılır?',
    answer: 'Hayır. Delüks adı tek başına belirli bir mesafe garantisi vermez. Otelin tam adını, konumunu ve Mescid-i Haram ya da Mescid-i Nebevi’ye yürüme veya servis düzenini tur detayından kontrol edin.'
  },
  {
    question: 'Delüks umre fiyatını hangi unsurlar etkiler?',
    answer: 'Seyahat tarihi, süre, oteller, oda yerleşimi, uçuş, transfer modeli, grup büyüklüğü ve özel hizmetler fiyatı etkiler. Güncel fiyat için seçilen programın canlı tur detayını veya yazılı teklifini esas alın.'
  },
  {
    question: 'Delüks umre rezervasyonundan önce ne istemeliyim?',
    answer: 'Otel ve uçuş adları, oda tipi, transferler, grup büyüklüğü, rehberlik, yemekler, dahil ve hariç hizmetler ile iptal koşullarını gösteren güncel programı ve sözleşmeyi inceleyin.'
  }
]

useSeoMeta({
  title: () => isSpecialPage.value ? specialPageSeo.value.title : (isDeluxeUmrahConcept.value ? deluxeSeo.value.title : undefined),
  ogTitle: () => isSpecialPage.value ? specialPageSeo.value.title : (isDeluxeUmrahConcept.value ? deluxeSeo.value.title : undefined),
  description: () => isSpecialPage.value ? specialPageSeo.value.description : (isDeluxeUmrahConcept.value ? deluxeSeo.value.description : undefined),
  ogDescription: () => isSpecialPage.value ? specialPageSeo.value.description : (isDeluxeUmrahConcept.value ? deluxeSeo.value.description : undefined),
  keywords: () => isDeluxeUmrahConcept.value ? deluxeSeo.value.keywords : undefined,
  ogType: () => isDeluxeUmrahConcept.value ? 'article' : (isSpecialPage.value ? 'website' : undefined),
  ogSiteName: () => (isDeluxeUmrahConcept.value || isSpecialPage.value) ? 'İnanç Turizm' : undefined,
  ogUrl: () => isSpecialPage.value ? `https://inanctur.com.tr${route.path}` : (isDeluxeUmrahConcept.value ? 'https://inanctur.com.tr/page/delux-umre-konsepti' : undefined),
  ogLocale: () => isSpecialPage.value ? (locale.value === 'en' ? 'en_US' : 'tr_TR') : (isDeluxeUmrahConcept.value ? 'tr_TR' : undefined),
  twitterCard: () => (isDeluxeUmrahConcept.value || isSpecialPage.value) ? 'summary_large_image' : undefined,
  twitterTitle: () => isSpecialPage.value ? specialPageSeo.value.title : (isDeluxeUmrahConcept.value ? deluxeSeo.value.title : undefined),
  twitterDescription: () => isSpecialPage.value ? specialPageSeo.value.description : (isDeluxeUmrahConcept.value ? deluxeSeo.value.description : undefined),
  robots: () => (isDeluxeUmrahConcept.value || isSpecialPage.value) ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' : undefined
})

useHead(() => {
  if (isSpecialPage.value) {
    const canonicalUrl = `https://inanctur.com.tr${route.path}`
    const documentItems = route.params?.link === 'belgeler' || route.params?.link === 'documents'
      ? [
          ['Hac Yetki Belgesi', 'Hajj authorisation document', 'HAC-2026-2027.png', 'image/png'],
          ['Umre Yetki Belgesi', 'Umrah authorisation document', 'umre-2026.png', 'image/png'],
          ['A Grubu Seyahat Acentası Belgesi', 'Travel agency operating document', 'A_GRUBU_ISLETME%20BELGESI_INANC_TURIZM.jpg', 'image/jpeg'],
          ['Diyanet Yetki Belgesi', 'Diyanet authorisation document', 'INANCTUR-2025-2026-UMRE-DIYANET-YETKI-BELGESI.webp', 'image/webp'],
          ['IATA Belgesi', 'IATA-related company document', 'IATA_inancturizm.jpg', 'image/jpeg'],
          ['TÜRSAB Üyelik Belgesi', 'TÜRSAB membership document', 'TURSAB_UYELIK_BELGESI.webp', 'image/webp']
        ].map(([name, description, filename, encodingFormat], index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'ImageObject',
            name,
            description,
            contentUrl: `https://inanctur.com.tr/assets/img/belgeler/${filename}`,
            encodingFormat
          }
        }))
      : []
    const guideItems = route.params?.link === 'ekibimiz'
      ? guides.map((guide, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Person',
            '@id': `https://inanctur.com.tr/rehberler/${guide.slug}#person`,
            name: guide.name,
            jobTitle: guide.title,
            url: `https://inanctur.com.tr/rehberler/${guide.slug}`,
            worksFor: { '@id': 'https://inanctur.com.tr/#organization' }
          }
        }))
      : []
    const graph = [
      {
        '@type': ['Organization', 'TravelAgency'],
        '@id': 'https://inanctur.com.tr/#organization',
        name: 'İnanç Turizm',
        alternateName: ['İnanç Tur', 'Inanc Tour'],
        url: 'https://inanctur.com.tr/',
        logo: 'https://inanctur.com.tr/assets/img/logo/inanc_tur_yeni_logo.webp',
        description: 'Hac, umre ve seyahat organizasyonları için turizm hizmetleri.',
        telephone: '+90 850 241 41 41',
        email: 'bilgi@inanctur.com.tr',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Mahmutbey Mah. Taşocağı Yolu Cad. No:1 Polat Tower & Residence Kat 24 D:188',
          addressLocality: 'Bağcılar',
          addressRegion: 'İstanbul',
          postalCode: '34218',
          addressCountry: 'TR'
        },
        identifier: {
          '@type': 'PropertyValue',
          name: 'TÜRSAB Belge No',
          value: '7677'
        }
      },
      {
        '@type': route.params?.link === 'umre-tur-fotograflari' || route.params?.link === 'umrah-tour-photos' ? ['CollectionPage', 'WebPage'] : 'WebPage',
        '@id': `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: specialPageSeo.value.title,
        description: specialPageSeo.value.description,
        inLanguage: locale.value === 'en' ? 'en' : 'tr',
        about: { '@id': 'https://inanctur.com.tr/#organization' },
        breadcrumb: { '@id': `${canonicalUrl}#breadcrumb` },
        mainEntity: documentItems.length
          ? { '@id': `${canonicalUrl}#documents` }
          : (guideItems.length ? { '@id': `${canonicalUrl}#guides` } : undefined)
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: locale.value === 'en' ? 'Home' : 'Ana Sayfa', item: 'https://inanctur.com.tr/' },
          { '@type': 'ListItem', position: 2, name: specialPageSeo.value.title, item: canonicalUrl }
        ]
      }
    ]
    if (documentItems.length) {
      graph.push({
        '@type': 'ItemList',
        '@id': `${canonicalUrl}#documents`,
        name: locale.value === 'en' ? 'Inanç Turizm documents' : 'İnanç Turizm belgeleri',
        itemListElement: documentItems
      })
    }
    if (guideItems.length) {
      graph.push({
        '@type': 'ItemList',
        '@id': `${canonicalUrl}#guides`,
        name: 'İnanç Turizm rehberleri',
        itemListElement: guideItems
      })
    }
    return {
      link: [{ rel: 'canonical', href: canonicalUrl }],
      script: [{
        key: 'company-information-schema',
        type: 'application/ld+json',
        children: JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
      }]
    }
  }

  if (!isDeluxeUmrahConcept.value) return {}

  const canonicalUrl = 'https://inanctur.com.tr/page/delux-umre-konsepti'
  const graph = [
    {
      '@type': ['Organization', 'TravelAgency'],
      '@id': 'https://inanctur.com.tr/#organization',
      name: 'İnanç Turizm',
      url: 'https://inanctur.com.tr/',
      identifier: {
        '@type': 'PropertyValue',
        name: 'TÜRSAB Belge No',
        value: '7677'
      }
    },
    {
      '@type': 'Article',
      '@id': `${canonicalUrl}#article`,
      headline: deluxeSeo.value.h1,
      description: deluxeSeo.value.description,
      mainEntityOfPage: { '@id': `${canonicalUrl}#webpage` },
      author: { '@id': 'https://inanctur.com.tr/#organization' },
      publisher: { '@id': 'https://inanctur.com.tr/#organization' },
      about: [
        { '@type': 'Thing', name: 'Delüks umre' },
        { '@type': 'Thing', name: 'Lüks umre' },
        { '@type': 'Thing', name: 'VIP umre' }
      ]
    },
    {
      '@type': 'WebPage',
      '@id': `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: deluxeSeo.value.title,
      description: deluxeSeo.value.description,
      breadcrumb: { '@id': `${canonicalUrl}#breadcrumb` },
      primaryImageOfPage: pageData.value?.SA_Fotograf
        ? { '@type': 'ImageObject', url: pageData.value.SA_Fotograf }
        : undefined
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${canonicalUrl}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: 'https://inanctur.com.tr/' },
        { '@type': 'ListItem', position: 2, name: 'Umre Rehberi', item: 'https://inanctur.com.tr/page/umre-rehberi' },
        { '@type': 'ListItem', position: 3, name: 'Delüks Umre Konsepti', item: canonicalUrl }
      ]
    },
    {
      '@type': 'FAQPage',
      '@id': `${canonicalUrl}#faq`,
      mainEntity: deluxeFaqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
      }))
    }
  ]

  return {
    link: [
      { rel: 'canonical', href: canonicalUrl }
    ],
    script: [{
      key: 'deluxe-umrah-concept-schema',
      type: 'application/ld+json',
      children: JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
    }]
  }
})
const practicalGuideRootId = 'c3RhYmlsUGFnZXN0cjU2MDExNTI5MjczODM2'
const guideRootIds = [
  'QmxvZ0thdGVnb3JpbGVydHIyNjUxNzQ5NDkyMjg0',
  'QmxvZ0thdGVnb3JpbGVydHI3MTQ0MTc1MzQ3NDMxOQqq',
  practicalGuideRootId
]
const locationGuideKind = computed(() => {
  const slug = String(route.params?.link || pageData.value?.SA_Link || '').toLocaleLowerCase('tr-TR')
  if (/^medine(?:-|$)/.test(slug) || /^madinah(?:-|$)/.test(slug)) return 'medine'
  if (/^mekke(?:-|$)/.test(slug) || /^makkah(?:-|$)/.test(slug)) return 'mekke'
  return ''
})
const isGuideInnerPage = computed(() => {
  if (isGuideLanding.value || isUmrahGuideLanding.value || !pageData.value) return false
  const parentId = pageData.value.SA_UstSayfa
  const categories = Array.isArray(pageData.value.pdb_categories)
    ? pageData.value.pdb_categories
    : [pageData.value.pdb_categories].filter(Boolean)
  return Boolean(locationGuideKind.value) || guideRootIds.includes(parentId) || categories.some(category => guideRootIds.includes(category))
})
const isPracticalGuideInnerPage = computed(() => {
  const categories = Array.isArray(pageData.value?.pdb_categories)
    ? pageData.value.pdb_categories
    : [pageData.value?.pdb_categories].filter(Boolean)
  return pageData.value?.SA_UstSayfa === practicalGuideRootId || categories.includes(practicalGuideRootId)
})
const innerGuideLabel = computed(() => isPracticalGuideInnerPage.value ? 'Umre Rehberi' : 'Umre Manevi Rehberi')
const guideStats = computed(() => [
  { value: parentPages.value.parent?.length || '6+', label: 'Rehber bölümü' },
  { value: '3', label: 'Yolculuk aşaması' },
  { value: '7/24', label: 'Manevi yol arkadaşı' }
])
const guideSteps = [
  { title: 'Yolculuk öncesi hazırlık', text: 'Niyet, evrak, bavul ve manevi hazırlık sürecini eksiksiz tamamlayın.' },
  { title: 'Kutsal topraklarda ibadet', text: 'Umre ve hac menâsikini doğru sıra ve anlamlarıyla takip edin.' },
  { title: 'Hatırayı hayata taşımak', text: 'Yolculuğun kazandırdığı huzuru ve güzel alışkanlıkları koruyun.' }
]
const umrahGuideShortcuts = [
  { icon: 'fa-solid fa-suitcase', title: 'Yolculuk hazırlığı', text: 'Evrak, bavul ve sağlık hazırlıkları' },
  { icon: 'fa-solid fa-kaaba', title: 'Umre ibadetleri', text: 'İhramdan tavafa adım adım' },
  { icon: 'fa-solid fa-map', title: 'Mekke ve Medine', text: 'Ziyaret yerleri ve pratik şehir bilgileri' },
  { icon: 'fa-solid fa-question-circle', title: 'Merak edilenler', text: 'En çok sorulan soruların cevapları' }
]

const featuredUmrahGuidePages = [
  {
    pdb_Id: 'featured-first-umrah',
    SA_Link: 'ilk-kez-umreye-gidecekler',
    SA_Baslik: 'İlk Kez Umreye Gidecekler İçin Rehber',
    SA_Subtitle: 'Tur seçiminden evrak ve valiz hazırlığına, mikattan tavafa ve sık yapılan hatalara kadar ilk yolculuğunuzu adım adım planlayın.',
    SA_CardImage: '/assets/img/guides/cards/ilk-kez-umreye-gidecekler.webp',
    isFeaturedGuide: true
  },
  {
    pdb_Id: 'featured-documents',
    SA_Link: 'umre-icin-gerekli-belgeler',
    SA_Baslik: 'Umre İçin Gerekli Belgeler, Vize ve Aşı',
    SA_Subtitle: 'Pasaport, vize, güncel sağlık şartları, ilaçlar ve çocuklarla seyahat için gerekli kontrolleri tek listede inceleyin.',
    SA_CardImage: '/assets/img/guides/cards/umre-gerekli-belgeler.webp',
    isFeaturedGuide: true
  },
  {
    pdb_Id: 'featured-nusuk-rawdah',
    SA_Link: 'nusuk-ravza-randevusu',
    SA_Baslik: 'Nusuk Uygulaması ve Ravza Randevusu',
    SA_Subtitle: 'Kafile Ravza randevusunu İnanç Tur’un nasıl aldığını ve Nusuk’un yalnızca ek randevu isteyen yolcular için kullanımını öğrenin.',
    SA_CardImage: '/assets/img/guides/cards/nusuk-ravza-randevusu.webp',
    isFeaturedGuide: true
  },
  {
    pdb_Id: 'featured-senior-umrah',
    SA_Link: 'yaslilar-icin-umre-rehberi',
    SA_Baslik: 'Yaşlılar İçin Umre Rehberi',
    SA_Subtitle: 'Otel mesafesi, günlük tempo, sağlık, ilaç, hareket desteği ve refakat planını yolcunun ihtiyacına göre değerlendirin.',
    SA_CardImage: '/assets/img/guides/cards/yaslilar-icin-umre-rehberi.webp',
    isFeaturedGuide: true
  },
  {
    pdb_Id: 'featured-family-umrah',
    SA_Link: 'cocukla-umre-rehberi',
    SA_Baslik: 'Çocukla ve Bebekle Umre Rehberi',
    SA_Subtitle: 'Aileye uygun dönem, otel, oda, uçuş, valiz ve kalabalıkta güvenlik planını rezervasyondan önce hazırlayın.',
    SA_CardImage: '/assets/img/guides/cards/cocukla-umre-rehberi.webp',
    isFeaturedGuide: true
  },
  {
    pdb_Id: 'featured-female-guidance',
    SA_Link: 'kadin-irsad-gorevlisi',
    SA_Baslik: 'Umrede Kadın İrşad Görevlisi Var mı?',
    SA_Subtitle: 'Ravza ziyaretindeki kadın görevli desteğini, tur boyunca görevlendirme durumunu ve mobil uygulamadaki kadınlara özel bilgileri inceleyin.',
    SA_CardImage: '/assets/img/guides/cards/kadin-irsad-gorevlisi.webp',
    isFeaturedGuide: true
  },
  {
    pdb_Id: 'featured-tour-selection',
    SA_Link: 'en-iyi-umre-turu-nasil-secilir',
    SA_Baslik: 'Umre Turu Tavsiye ve Seçim Rehberi',
    SA_Subtitle: 'Acenta, otel, uçuş, rehberlik, sözleşme ve fiyat kapsamını ölçülebilir kriterlerle karşılaştırın.',
    SA_CardImage: '/assets/img/guides/cards/umre-turu-secim-rehberi.webp',
    isFeaturedGuide: true
  },
  {
    pdb_Id: 'featured-hotel-guide',
    SA_Link: 'umre-otelleri-rehberi',
    SA_Baslik: 'Mekke ve Medine Umre Otelleri Rehberi',
    SA_Subtitle: 'Otelleri Haremeyn mesafesi, gerçek ulaşım biçimi, oda, yemek planı ve gece sayısına göre değerlendirin.',
    SA_CardImage: '/assets/img/guides/cards/umre-otelleri-rehberi.webp',
    isFeaturedGuide: true
  },
  {
    pdb_Id: 'featured-deluxe-guide',
    SA_Link: 'delux-umre-konsepti',
    SA_Baslik: 'Delüks Umre Konsepti Nedir?',
    SA_Subtitle: 'Delüks, lüks, 5 yıldızlı, VIP ve butik umre kavramlarının farklarını ve hizmet kapsamını öğrenin.',
    SA_CardImage: '/assets/img/guides/cards/deluks-umre-konsepti.webp',
    isFeaturedGuide: true
  }
]

const umrahGuidePages = computed(() => {
  const apiPages = Array.isArray(parentPages.value.parent) ? parentPages.value.parent : []
  if (locale.value !== 'tr') return apiPages

  const apiSlugs = new Set(apiPages.map(page => page.SA_Link))
  const featuredPages = featuredUmrahGuidePages.filter(page => !apiSlugs.has(page.SA_Link))
  return [...featuredPages, ...apiPages]
})

const breadcrumb = ref([])
const createJourneyBreadcrumb = (parentText, parentLink) => {
  const currentTitle = String(pageData.value?.SA_Baslik || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return [
    { text: t('Anasayfa'), link: '/' },
    {
      text: locale.value === 'en' ? 'Journey Centre' : 'Yolculuk Merkezi',
      link: '/page/yolculuk-merkezi'
    },
    { text: parentText, link: parentLink },
    ...(currentTitle ? [{ text: currentTitle, link: null }] : [])
  ]
}
const setBreadcrumb = ()=>{
  // console.log("ageData.value.",pageData.value)
  if(pageData.value &&  pageData.value?.SA_UstSayfa == "QmxvZ0thdGVnb3JpbGVydHIyNjUxNzQ5NDkyMjg0") {
    breadcrumb.value = createJourneyBreadcrumb(t('Umre Manevi Rehberi'), '/page/umre-ve-hac-rehberi');
        pageStyle.value = 'split';
        getSubPages('QmxvZ0thdGVnb3JpbGVydHIyNjUxNzQ5NDkyMjg0');
  }else if(pageData.value &&  pageData.value?.SA_UstSayfa == "QmxvZ0thdGVnb3JpbGVydHI3MTQ0MTc1MzQ3NDMxOQqq") {
    breadcrumb.value = createJourneyBreadcrumb(t('Umre Manevi Rehberi'), '/page/umre-ve-hac-rehberi');
        pageStyle.value = 'split';
        getSubPages('QmxvZ0thdGVnb3JpbGVydHI3MTQ0MTc1MzQ3NDMxOQqq');
  }else  if(pageData.value &&  pageData.value?.SA_UstSayfa == "c3RhYmlsUGFnZXN0cjU2MDExNTI5MjczODM2") {
    breadcrumb.value = createJourneyBreadcrumb(t('Umre Rehberi'), '/page/umre-rehberi');
  }else  if(pageData.value &&  pageData.value?.SA_UstSayfa == "QmxvZ0thdGVnb3JpbGVydHI3MTQ0MTc1MzQ3NDMxOQqq") {
    breadcrumb.value = createJourneyBreadcrumb(t('Medine Rehberi'), '/page/medine-rehberi');
  }else  if(pageData.value &&  pageData.value?.SA_UstSayfa == "QmxvZ0thdGVnb3JpbGVydHI5MzA4MTc1MzQ3NDI3Nwqq") {
    breadcrumb.value = createJourneyBreadcrumb(t('Mekke Rehberi'), '/page/mekke-rehberi');

  }else  if(pageData.value &&  ["QmxvZ0thdGVnb3JpbGVydHI2ODkyMTc1NDI5NTY0NQqq","QmxvZ0thdGVnb3JpbGVydHI1MTM1MTc1NDI5MjM3OQqq"].includes(pageData.value?.SA_UstSayfa)) {
    breadcrumb.value = createJourneyBreadcrumb(t('Medine Rehberi'), '/page/medine-rehberi');
         getSubPages('QmxvZ0thdGVnb3JpbGVydHI3MTQ0MTc1MzQ3NDMxOQqq');
     getGuide("QmxvZ0thdGVnb3JpbGVydHI3MTQ0MTc1MzQ3NDMxOQqq");
  } else if (pageData.value?.pdb_categories && pageData.value?.pdb_categories?.includes?.("QmxvZ0thdGVnb3JpbGVyMTc1MzQ3NDI3Ngqq")) {
    breadcrumb.value = createJourneyBreadcrumb(t('Mekke Rehberi'), '/page/mekke-rehberi');
     getSubPages('QmxvZ0thdGVnb3JpbGVydHI5MzA4MTc1MzQ3NDI3Nwqq');
     getGuide("QmxvZ0thdGVnb3JpbGVydHI5MzA4MTc1MzQ3NDI3Nwqq");
  } else if (isGuideInnerPage.value) {
    breadcrumb.value = isPracticalGuideInnerPage.value
      ? createJourneyBreadcrumb(t('Umre Rehberi'), '/page/umre-rehberi')
      : createJourneyBreadcrumb(t('Umre Manevi Rehberi'), '/page/umre-ve-hac-rehberi');
  }   else {
    breadcrumb.value = [
      { text: t('Anasayfa'), link: '/' }
    ];
  }

  // Mekke/Medine yazılarında üst sayfa kimliği değişse bile konum rehberi hiyerarşisini koru.
  if (locationGuideKind.value === 'medine') {
    breadcrumb.value = createJourneyBreadcrumb(t('Medine Rehberi'), '/page/medine-rehberi');
  } else if (locationGuideKind.value === 'mekke') {
    breadcrumb.value = createJourneyBreadcrumb(t('Mekke Rehberi'), '/page/mekke-rehberi');
  }
 
}
const nextPath = computed(() => {
  if(AllSubPages.value.length === 0 || !pageData.value) {
    return '';
  } 
  let nextPage = null;
  let currentIndex = AllSubPages.value.findIndex(item => item.pdb_Id === pageData.value.wa_sayfa_Id);
  if (currentIndex !== -1 && currentIndex < AllSubPages.value.length - 1) {
    nextPage = AllSubPages.value[currentIndex + 1];
    console.log("nextPage",nextPage)
    return getPath('/page/' + nextPage.SA_Link);
  }
  // If there is no next page, return an empty string or a default path
  // return getPath('/page/' + AllSubPages.value[0].SA_Link);
  // return 
  return '';
})
onMounted(() => {
  if (!isFaqPage.value && !isJourneyHub.value) {
    getParent("parent")
    getParent("similar");
    setBreadcrumb();
  }
  url.value = window.location.href;
})
</script>
